import type { NextScreening } from '@/api/types'

/** A wave is only reported once it is this many minutes late; a shorter delay is normal start-up time. */
export const DELAY_REPORT_MINUTES = 10
/** Started for more than this share of the customers is a warning; at or under it, or for nobody, it is critical. */
export const MOSTLY_STARTED_SHARE = 0.5

const TIME_SPAN = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})/

/** A .NET TimeSpan ("d.hh:mm:ss.fffffff" or "hh:mm:ss") as whole minutes; seconds are dropped. */
export function delayMinutes(value: string | null): number {
  const match = value ? TIME_SPAN.exec(value) : null
  return match ? Number(match[1] ?? 0) * 1440 + Number(match[2]) * 60 + Number(match[3]) : 0
}

/** "4h 17m", or "12m 4s" under an hour; "–" when there is no value. */
export function formatDuration(value: string | null): string {
  if (!value) return '–'
  const match = TIME_SPAN.exec(value)
  if (!match) return value
  const hours = Number(match[2]) + Number(match[1] ?? 0) * 24
  const minutes = Number(match[3])
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${Number(match[4])}s`
}

export type DelayLevel = 'warning' | 'critical'

/**
 * How loudly a late wave is reported, or null while the delay is short. What matters is how much of the wave has
 * started: nobody started, or half or less, is critical; more than half started is a warning.
 */
export function delayLevel(wave: Pick<NextScreening, 'delayTime' | 'customers' | 'runningCustomers'>): DelayLevel | null {
  if (delayMinutes(wave.delayTime) < DELAY_REPORT_MINUTES) return null
  const started = wave.customers > 0 ? wave.runningCustomers / wave.customers : 0
  return started > MOSTLY_STARTED_SHARE ? 'warning' : 'critical'
}

/** "has not started for any of the 40 customers", or "has not started for 13 of 40 customers". */
export function notStartedSummary(wave: Pick<NextScreening, 'customers' | 'runningCustomers' | 'notRunningCustomers'>): string {
  return wave.runningCustomers === 0
    ? `has not started for any of the ${wave.customers} customers`
    : `has not started for ${wave.notRunningCustomers} of ${wave.customers} customers`
}
